import { Injectable } from '@angular/core';
import { ApiService } from '../../http/api.service';

declare global {
  interface Window {
    SupportWidget?: {
      open: () => void;
      close: () => void;
      identify: (user: any) => void;
    };
  }
}

// Parámetro que la app Android agrega a la URL que le entregamos (?channel=app) para
// que el widget sepa que se está escribiendo desde la app y no desde el navegador.
// Se persiste porque la app puede navegar internamente (login, redirects) sin volver
// a incluir el parámetro en cada carga.
const CLIENT_CHANNEL_PARAM = 'channel';
const CLIENT_CHANNEL_STORAGE_KEY = 'app.supportWidgetChannel';

@Injectable({
  providedIn: 'root'
})
export class SupportWidgetService {

  constructor(private api: ApiService) {}

  // Debe llamarse siempre al iniciar la app (haya o no sesión), porque si el usuario no
  // está logueado el router puede redirigir a /login y perder ?channel=app de la URL
  // antes de que identifyLoggedUser() llegue a leerlo.
  captureClientChannelFromUrl(): void {
    this._readClientChannel();
  }

  // Identifica al usuario logueado ante el widget de soporte (nombre/email visibles para el agente)
  identifyLoggedUser(): void {
    this.api.get('api/SupportWidget/identity').subscribe({
      next: (identity) => {
        console.log('[SupportWidget] identidad recibida de api/SupportWidget/identity:', identity);
        window.SupportWidget?.identify(this._withAppChannelMarker(identity));
      },
      error: (err) => {
        // el chat sigue funcionando como anónimo si falla, pero se deja registro para diagnosticar
        console.error('[SupportWidget] no se pudo obtener la identidad, el chat quedará anónimo:', err);
      }
    });
  }

  // El proveedor del widget solo reconoce {id, name, email, hash} en identify() y
  // descarta cualquier otro campo (ver widget.js: identify() e initState(), donde el
  // canal viaja hardcodeado como "channel=web" al conectar el WebSocket, sin forma de
  // configurarlo desde el sitio anfitrión). Hasta que el proveedor exponga un canal
  // configurable, el origen se anexa al nombre visible para el agente.
  private _withAppChannelMarker(identity: any): any {
    if (!identity?.name || this._readClientChannel() !== 'app') return identity;
    return { ...identity, name: `${identity.name} · App` };
  }

  private _readClientChannel(): string | null {
    try {
      const fromUrl = new URLSearchParams(window.location.search).get(CLIENT_CHANNEL_PARAM);
      if (fromUrl) {
        localStorage.setItem(CLIENT_CHANNEL_STORAGE_KEY, fromUrl);
        return fromUrl;
      }
      return localStorage.getItem(CLIENT_CHANNEL_STORAGE_KEY);
    } catch (e) {
      return null; // localStorage no disponible (modo privado, etc.)
    }
  }

  // Revierte a anónimo y cierra el panel (logout). Además borra el historial de
  // conversación que el widget persiste en localStorage, para que en un equipo
  // compartido la siguiente persona que entre no vea la conversación anterior.
  reset(): void {
    this._clearWidgetStorage();
    window.SupportWidget?.identify({});
    window.SupportWidget?.close();
  }

  private _clearWidgetStorage(): void {
    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith('sw.'))
        .forEach((key) => localStorage.removeItem(key));
    } catch (e) { /* localStorage no disponible (modo privado, etc.) */ }
  }
}
